import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import { AdElement, isTextElement, parseElementName } from '@adflow/types';
import { resolveElementVariables, buildBrandVariablesMap } from '@adflow/variable-resolvers';
import { svgUtils } from '@adflow/utils';

import type { AdResolutionFontDescriptor } from './types';

export type ResolveAdElementsCoreArgs = {
  elements: AdElement[];
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: readonly AdResolutionFontDescriptor[];
  customizations?: Record<string, string> | null;
};

/** Pure pipeline before data-point mapping: background, BrandKit, customizations, preview font prefix. */
export function resolveAdElementsCore({
  elements,
  brandKit,
  fonts,
  customizations,
}: ResolveAdElementsCoreArgs): AdElement[] {
  const brandVariables = buildBrandVariablesMap(brandKit, [...fonts]) ?? undefined;
  const withBackground = svgUtils.enrichRootBackgroundVectors(elements);
  let resolved = brandVariables
    ? resolveElementVariables(withBackground, brandVariables)
    : withBackground;

  if (customizations && Object.keys(customizations).length > 0) {
    resolved = resolved.map((el) => {
      const override = customizations[parseElementName(el.name).name];
      if (override !== undefined && isTextElement(el)) {
        return {
          ...el,
          data: { ...el.data, content: override },
        };
      }
      return el;
    });
  }

  return resolved.map((el) => {
    if (isTextElement(el) && el.data.style.fontFamily) {
      return {
        ...el,
        data: {
          ...el.data,
          style: {
            ...el.data.style,
            fontFamily: `adflow-preview-${el.data.style.fontFamily}`,
          },
        },
      };
    }
    return el;
  });
}

export type { ConsoleAPIGraph, CreativeHubTypes };
